import { useLeadsRetrieve, type LeadDetail } from '@gta/api-client'
import { Link, createFileRoute, stripSearchParams, useCanGoBack, useRouter } from '@tanstack/react-router'
import { ArrowLeft, Pencil } from 'lucide-react'

import { DetailSkeleton, ErrorState } from '@/components/console/states'
import { StatusBadge } from '@/components/console/status-badge'
import { PageContainer } from '@/components/layout/page-container'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { asApiError, errorMessageOf, unwrapResponse } from '@/features/leads/api-utils'
import {
  LEAD_POOL_STATE_LABELS,
  LEAD_POOL_STATE_TONE,
  isLeadPoolState,
  type LeadDetailTab,
} from '@/features/leads/constants'
import { CopyButton } from '@/features/leads/copy-button'
import { LeadActivityPanel } from '@/features/leads/lead-activity-panel'
import { LeadAssignmentTimeline } from '@/features/leads/lead-assignment-timeline'
import { LeadOwnerSelect } from '@/features/leads/lead-owner-select'
import { LeadPoolDispatchDialog } from '@/features/leads/lead-pool-dispatch-dialog'
import { LeadProfileCard } from '@/features/leads/lead-profile-card'
import { LeadRecallDialog } from '@/features/leads/lead-recall-dialog'
import { LeadStageBadge } from '@/features/leads/lead-stage-badge'
import { LeadStageTimeline } from '@/features/leads/lead-stage-timeline'
import { LeadTransitionActions } from '@/features/leads/lead-transition-actions'
import { poolDurationText } from '@/features/leads/lead-pool'
import { WonReceiptSection } from '@/features/leads/won-receipt-section'
import { LEAD_DETAIL_STRIP_DEFAULTS, leadDetailSearchSchema } from '@/features/leads/search-schema'
import { useMetaEnums } from '@/features/leads/use-meta-enums'
import { useRoleAccess } from '@/lib/role-access'

/**
 * 线索详情页。
 *
 * tab 状态进 search param(`?tab=timeline` 冷加载直接落在阶段时间线上);
 * 返回列表走 history.back(),这样列表的筛选/分页/排序原样还原 —— 面包屑的 /leads 是「回到默认视图」的入口。
 * 默认 tab 由 stripSearchParams 摘掉(A4 全站统一),详情页裸地址保持干净。
 */
export const Route = createFileRoute('/_authed/leads/$leadId')({
  validateSearch: leadDetailSearchSchema,
  search: {
    middlewares: [stripSearchParams(LEAD_DETAIL_STRIP_DEFAULTS)],
  },
  component: LeadDetailPage,
})

function BackToListButton() {
  const router = useRouter()
  const canGoBack = useCanGoBack()

  if (canGoBack) {
    return (
      <Button
        variant="outline"
        onClick={() => {
          router.history.back()
        }}
      >
        <ArrowLeft />
        返回列表
      </Button>
    )
  }

  return (
    <Button variant="outline" asChild>
      <Link to="/leads" search={{}}>
        <ArrowLeft />
        返回列表
      </Link>
    </Button>
  )
}


function LeadDetailHeader({ lead, stageLabel }: { lead: LeadDetail; stageLabel: (value: string | undefined) => string }) {
  // ★W11:池态徽标。它由派发史链尾判(后端 pool_state,LD-7),不是 Lead 上的状态列 ——
  // 「被收回」与「从没派过」在主档上逐字节相同,不标出来就永远只能看到「未分配」。
  const poolState = isLeadPoolState(lead.pool_state) ? lead.pool_state : null

  return (
    <div className="flex flex-wrap items-center gap-2">
      <h2 className="text-lg font-medium text-console-text">{lead.customer.name}</h2>
      <LeadStageBadge stage={lead.status} label={lead.status_display || stageLabel(lead.status)} />
      {poolState && (
        <StatusBadge tone={LEAD_POOL_STATE_TONE[poolState]}>
          总池子 · {LEAD_POOL_STATE_LABELS[poolState]}
          {poolState === 'recalled' && ` ${poolDurationText(lead.pooled_since)}`}
        </StatusBadge>
      )}
      <span className="flex items-center gap-0.5 rounded-md bg-console-hover px-2 py-0.5 font-mono text-xs text-console-muted">
        {lead.resource_id}
        <CopyButton value={lead.resource_id} />
      </span>
    </div>
  )
}

/**
 * 归属动作区(★W11):在池里出「再派发」,有归属出「收回到总池子」。
 *
 * **主管及以上才渲染**(LD-9:收回是管理动作,恒由人触发)。前端 gate 是体验不是安全边界
 * —— 后端 `required_role` 同挂,手敲接口仍 403;这里只是别把点了必然 403 的按钮画出来。
 */
function LeadAssignmentActions({ lead, meta }: { lead: LeadDetail; meta: ReturnType<typeof useMetaEnums> }) {
  const { isManager } = useRoleAccess()
  if (!isManager) return null

  const terminal = lead.status === 'won' || lead.status === 'lost'

  if (lead.owner_employee === null) {
    // 终态线索不该进池,也就不该被从池里派出去(后端同判,400)—— 这里直接不画按钮。
    return terminal ? null : (
      <LeadPoolDispatchDialog
        leadResourceId={lead.resource_id}
        meta={meta}
        previousOwnerDisplayName={lead.previous_owner_display_name}
        size="sm"
      />
    )
  }

  return (
    <LeadRecallDialog
      leadResourceId={lead.resource_id}
      ownerDisplayName={lead.owner_employee.display_name}
      terminal={terminal}
      size="sm"
    />
  )
}

function LeadDetailPage() {
  const { leadId } = Route.useParams()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const meta = useMetaEnums()

  const query = useLeadsRetrieve(leadId)
  const lead = unwrapResponse<LeadDetail>(query.data)

  const notFound = asApiError(query.error)?.status === 404

  return (
    <PageContainer
      title={lead ? `线索 · ${lead.customer.name}` : '线索详情'}
      breadcrumbs={[{ label: '线索管理', to: '/leads' }, { label: leadId }]}
      actions={
        <>
          <BackToListButton />
          {/* 编辑只覆盖客咨要素与归属;阶段流转仍只走下方的推进按钮(A7)。 */}
          <Button asChild>
            <Link to="/leads/$leadId/edit" params={{ leadId }}>
              <Pencil />
              编辑
            </Link>
          </Button>
        </>
      }
    >
      {query.isLoading && <DetailSkeleton fields={16} />}

      {query.isError && (
        <ErrorState
          title={notFound ? '线索不存在' : '线索详情加载失败'}
          message={
            notFound ? `资源号「${leadId}」在库中查不到,可能已被清理或链接有误。` : errorMessageOf(query.error)
          }
          action={
            <Button variant="outline" size="sm" asChild>
              <Link to="/leads" search={{}}>
                回到线索列表
              </Link>
            </Button>
          }
        />
      )}

      {lead && (
        <>
          <section className="console-card flex flex-col gap-3 px-4 py-3.5">
            <LeadDetailHeader lead={lead} stageLabel={meta.stageLabel} />

            <div className="flex flex-wrap items-end justify-between gap-3 border-t border-console-divider pt-3">
              <div className="flex flex-col gap-1.5">
                <span className="text-xs text-console-muted">负责人</span>
                <div className="flex flex-wrap items-center gap-2">
                  <LeadOwnerSelect lead={lead} meta={meta} />
                  <LeadAssignmentActions lead={lead} meta={meta} />
                </div>
              </div>
              <LeadTransitionActions lead={lead} stageLabel={meta.stageLabel} meta={meta} />
            </div>
          </section>

          <LeadProfileCard lead={lead} inboxBaseUrl={meta.inboxBaseUrl} />

          {/* ★W15(R3-B):成单区块。未成单时整块不渲染,故它不占未成单线索的版面。 */}
          <WonReceiptSection lead={lead} meta={meta} />

          <section className="console-card px-4 py-3">
            <Tabs
              value={search.tab}
              onValueChange={(value) => {
                void navigate({ search: (prev) => ({ ...prev, tab: value as LeadDetailTab }), replace: true })
              }}
            >
              {/* ★W11(LD-21):派发史与阶段史是两条正交的轨,故并列成 tab 而不是上下堆叠
                  ——两者行数同量级,堆叠会把详情页拉得很长。 */}
              <TabsList variant="line" className="border-b border-console-border pb-1.5">
                <TabsTrigger value="activities">跟进记录({lead.activities.length})</TabsTrigger>
                <TabsTrigger value="timeline">阶段时间线({lead.stage_history.length})</TabsTrigger>
                <TabsTrigger value="dispatch">派发时间线({lead.assignment_history.length})</TabsTrigger>
              </TabsList>

              <TabsContent value="activities" className="pt-3">
                <LeadActivityPanel lead={lead} meta={meta} />
              </TabsContent>

              <TabsContent value="timeline" className="pt-3">
                <LeadStageTimeline lead={lead} meta={meta} />
              </TabsContent>

              <TabsContent value="dispatch" className="pt-3">
                <LeadAssignmentTimeline lead={lead} />
              </TabsContent>
            </Tabs>
          </section>
        </>
      )}
    </PageContainer>
  )
}
